import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMXN, imageUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, TrendUp, TrendDown, Users, Timer, Trophy, Eye, ChatCircleText,
  Warning, Info, ArrowUpRight, Handshake, Trophy as TrophyIcon, XCircle, Clock,
  MapPin, House, Lightbulb, ChartLine,
} from "@phosphor-icons/react";

const STATUS_META = {
  nuevo:          { label: "Nuevo",          color: "#C8A24A", accent: "bg-[#C8A24A]" },
  contactado:     { label: "Contactado",     color: "#38BDF8", accent: "bg-sky-400" },
  visita:         { label: "Visita",         color: "#A78BFA", accent: "bg-violet-400" },
  en_negociacion: { label: "Negociación",    color: "#F59E0B", accent: "bg-amber-400" },
  ganado:         { label: "Ganado",         color: "#34D399", accent: "bg-emerald-400" },
  perdido:        { label: "Perdido",        color: "#71717A", accent: "bg-zinc-400" },
};

const INSIGHT_META = {
  urgent: { icon: Warning, chip: "border-red-500/40 bg-red-500/10 text-red-300" },
  warn:   { icon: Warning, chip: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  info:   { icon: Info,    chip: "border-[#C8A24A]/40 bg-[#C8A24A]/10 text-[#C8A24A]" },
};

const formatHours = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} días`;
};

export function AnalyticsDashboard({ scope = "personal" }) {
  const [overview, setOverview] = useState(null);
  const [top, setTop] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [days, setDays] = useState(30);
  const [agentFilter, setAgentFilter] = useState(""); // agency scope only
  const [agencyMembers, setAgencyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const basePath = scope === "agency" ? "/agencies/me/analytics" : "/analytics";

  // Load agency team once for filter dropdown
  useEffect(() => {
    if (scope !== "agency") return;
    api.get("/agencies/me").then(({ data }) => {
      if (data?.members) setAgencyMembers(data.members);
    }).catch(() => {});
  }, [scope]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (scope === "agency" && agentFilter) params.agent_id = agentFilter;
      const [ovr, topRes, evo] = await Promise.all([
        api.get(`${basePath}/overview`, { params: agentFilter ? { agent_id: agentFilter } : {} }),
        api.get(`${basePath}/top-properties`, { params: { limit: 5, ...(agentFilter ? { agent_id: agentFilter } : {}) } }),
        api.get(`${basePath}/evolution`, { params }),
      ]);
      setOverview(ovr.data);
      setTop(topRes.data);
      setEvolution(evo.data);
    } catch (err) {
      setError(formatApiError(err, "No pudimos cargar las analíticas"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, agentFilter, scope]);

  if (loading && !overview) return (
    <div className="max-w-6xl mx-auto px-6 py-12 space-y-6 animate-pulse">
      <div className="h-12 w-64 bg-[#1A1A1A] rounded" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[0,1,2,3].map(i => <div key={i} className="h-28 bg-[#1A1A1A] rounded-2xl" />)}
      </div>
      <div className="h-64 bg-[#1A1A1A] rounded-2xl" />
    </div>
  );
  if (error) return (
    <div className="max-w-6xl mx-auto px-6 py-24 text-center">
      <p className="text-destructive">{error}</p>
    </div>
  );
  if (!overview) return null;

  const trend = overview.trends?.leads_trend_pct;
  const trendPositive = trend != null && trend > 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <Link to={scope === "agency" ? "/dashboard/agencia" : "/dashboard"} className="text-xs text-[#C8A24A]/70 hover:text-[#C8A24A] inline-flex items-center gap-1" data-testid="analytics-back-link">
            <ArrowLeft size={12} /> {scope === "agency" ? "Panel de agencia" : "Panel"}
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold mt-2">
            {scope === "agency" ? "Inteligencia comercial · Agencia" : "Inteligencia comercial"}
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-1 tracking-tight" data-testid="analytics-title">
            {scope === "agency" ? "Desempeño del equipo" : "¿Qué está funcionando?"}
          </h1>
          <p className="text-sm text-[#C8A24A]/70 mt-2">
            {scope === "agency"
              ? "Vista consolidada del equipo. Filtra por asesor para diagnóstico y coaching."
              : "Métricas accionables para decidir tu próximo movimiento."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {scope === "agency" && agencyMembers.length > 0 && (
            <select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              data-testid="analytics-agent-filter"
              className="h-9 px-3 bg-[#111111] border border-[#C8A24A]/20 rounded-lg text-xs text-[#C8A24A]"
            >
              <option value="">Todo el equipo</option>
              {agencyMembers.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}
          {[7, 30, 90].map(n => (
            <button
              key={n}
              onClick={() => setDays(n)}
              data-testid={`analytics-range-${n}`}
              className={`h-9 px-3 rounded-lg text-xs font-semibold border transition-colors ${
                days === n ? "border-[#C8A24A] bg-[#C8A24A]/10 text-[#C8A24A]" : "border-[#C8A24A]/20 text-[#C8A24A]/60 hover:border-[#C8A24A]/40"
              }`}
            >
              {n === 7 ? "7 días" : n === 30 ? "30 días" : "90 días"}
            </button>
          ))}
        </div>
      </div>

      {/* Actionable insights */}
      {overview.insights?.length > 0 && (
        <section className="mb-8" data-testid="analytics-insights">
          <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-3 inline-flex items-center gap-1.5">
            <Lightbulb size={11} weight="fill" /> Qué acción tomar ahora
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {overview.insights.map((ins, i) => {
              const m = INSIGHT_META[ins.level] || INSIGHT_META.info;
              const Icon = m.icon;
              return (
                <Link
                  key={i}
                  to={ins.cta_href || "/dashboard"}
                  data-testid={`insight-${ins.level}-${i}`}
                  className={`p-4 rounded-2xl border ${m.chip} flex items-start gap-3 hover:brightness-110 transition-all group`}
                >
                  <Icon size={20} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{ins.title}</p>
                    <p className="text-xs opacity-80 mt-1">{ins.action}</p>
                  </div>
                  <ArrowUpRight size={16} className="opacity-60 group-hover:opacity-100 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* KPI grid */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="analytics-kpis">
        <KpiCard
          icon={Users}
          label="Total de leads"
          value={overview.totals.total_leads}
          testId="kpi-total-leads"
          trend={trend}
          trendLabel={trend != null ? "vs. semana pasada" : null}
        />
        <KpiCard
          icon={Timer}
          label="Tiempo de respuesta"
          value={formatHours(overview.response.avg_hours)}
          testId="kpi-response"
          hint={overview.response.grade ? gradeHint(overview.response.grade) : "Sin datos aún"}
        />
        <KpiCard
          icon={Trophy}
          label="Tasa de cierre"
          value={`${overview.totals.win_rate}%`}
          testId="kpi-win-rate"
          hint={`${overview.totals.ganado} ganados · ${overview.totals.perdido} perdidos`}
          tint="emerald"
        />
        <KpiCard
          icon={Eye}
          label="Vistas totales"
          value={overview.totals.total_views}
          testId="kpi-views"
          hint="Suma de vistas en tus publicaciones"
        />
      </section>

      {/* Funnel */}
      <section className="mb-8 bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-5 sm:p-6" data-testid="analytics-funnel">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-2">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Funnel de conversión</p>
            <h2 className="font-heading text-2xl mt-1">¿Dónde se pierden tus clientes?</h2>
          </div>
        </div>
        <FunnelChart funnel={overview.funnel} />
      </section>

      {/* Top properties */}
      <section className="grid md:grid-cols-2 gap-5 mb-8">
        <TopList
          title="Con más vistas"
          subtitle="¿Qué genera interés?"
          icon={Eye}
          items={top?.by_views || []}
          metricKey="views"
          metricLabel="vistas"
          testId="top-by-views"
        />
        <TopList
          title="Con más contactos"
          subtitle="¿Qué convierte a lead?"
          icon={ChatCircleText}
          items={top?.by_contacts || []}
          metricKey="contacts"
          metricLabel="contactos"
          testId="top-by-contacts"
        />
      </section>

      {/* Evolution */}
      <section className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-5 sm:p-6 mb-4" data-testid="analytics-evolution">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-2">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold inline-flex items-center gap-1.5">
              <ChartLine size={11} weight="fill" /> Evolución
            </p>
            <h2 className="font-heading text-2xl mt-1">¿Cuándo llegan los leads?</h2>
          </div>
        </div>
        <EvolutionChart series={evolution?.series || []} />
      </section>
    </div>
  );
}

function gradeHint(g) {
  const map = {
    excelente: "Excelente — mantenlo así",
    buena: "Buena — puedes mejorar",
    mejorable: "Mejorable — apunta a < 2 h",
    lenta: "Lenta — pierdes leads",
  };
  return map[g] || "";
}

function KpiCard({ icon: Icon, label, value, hint, trend, trendLabel, testId, tint = "gold" }) {
  const tintMap = {
    gold:    { icon: "text-[#C8A24A]",  border: "border-[#C8A24A]/20" },
    emerald: { icon: "text-emerald-400", border: "border-emerald-500/20" },
  };
  const t = tintMap[tint] || tintMap.gold;
  const trendPositive = trend != null && trend > 0;
  return (
    <div className={`p-4 rounded-2xl bg-[#1A1A1A] border ${t.border}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <Icon size={20} weight="duotone" className={t.icon} />
        {trend != null && (
          <span
            className={`inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${
              trendPositive ? "text-emerald-300 bg-emerald-500/10" : trend < 0 ? "text-red-300 bg-red-500/10" : "text-[#C8A24A]/60 bg-[#C8A24A]/10"
            }`}
            data-testid={`${testId}-trend`}
          >
            {trendPositive ? <TrendUp size={10} weight="bold" /> : trend < 0 ? <TrendDown size={10} weight="bold" /> : null}
            {trend > 0 ? "+" : ""}{trend}%
          </span>
        )}
      </div>
      <p className="text-xs uppercase tracking-wider mt-3 text-[#C8A24A]/60 font-semibold">{label}</p>
      <p className="text-2xl font-heading font-bold mt-0.5">{value}</p>
      {hint && <p className="text-xs text-[#C8A24A]/50 mt-1">{hint}</p>}
      {trendLabel && <p className="text-xs text-[#C8A24A]/40 mt-0.5">{trendLabel}</p>}
    </div>
  );
}

function FunnelChart({ funnel }) {
  const max = Math.max(1, ...funnel.map(s => s.count));
  return (
    <div className="space-y-2.5" data-testid="funnel-stages">
      {funnel.map((step, i) => {
        const meta = STATUS_META[step.key];
        const width = Math.max(6, Math.round((step.count / max) * 100));
        return (
          <div key={step.key} data-testid={`funnel-step-${step.key}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold" style={{ color: meta.color }}>
                {meta.label}
              </span>
              <span className="text-[#C8A24A]/60 flex items-center gap-2">
                {i > 0 && step.conversion_from_prev != null && (
                  <span className="text-xs font-mono" data-testid={`funnel-conv-${step.key}`}>
                    ↳ {step.conversion_from_prev}%
                  </span>
                )}
                <span className="font-bold text-[#C8A24A]">{step.count}</span>
              </span>
            </div>
            <div className="h-3 bg-[#0A0A0A] rounded-full overflow-hidden border border-[#C8A24A]/10">
              <div
                className="h-full rounded-full transition-[width] duration-700"
                style={{ width: `${width}%`, backgroundColor: meta.color, opacity: step.count === 0 ? 0.25 : 1 }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopList({ title, subtitle, icon: Icon, items, metricKey, metricLabel, testId }) {
  return (
    <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-5 sm:p-6" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-[#C8A24A]/10 flex items-center justify-center flex-shrink-0">
          <Icon size={18} weight="duotone" className="text-[#C8A24A]" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">{subtitle}</p>
          <h3 className="font-heading text-lg">{title}</h3>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-8 text-xs text-[#C8A24A]/50" data-testid={`${testId}-empty`}>
          Aún no hay datos suficientes. Publica y comparte para empezar a medir.
        </div>
      ) : (
        <ol className="space-y-2" data-testid={`${testId}-list`}>
          {items.map((it, idx) => (
            <li key={it.id} data-testid={`${testId}-item-${it.id}`}>
              <Link
                to={`/dashboard/editar/${it.id}`}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#C8A24A]/[0.05] transition-colors"
              >
                <span className="w-6 h-6 rounded-md bg-[#0A0A0A] border border-[#C8A24A]/20 flex items-center justify-center text-xs font-heading font-bold text-[#C8A24A]/80 flex-shrink-0">
                  {idx + 1}
                </span>
                <div className="w-10 h-10 rounded-md overflow-hidden bg-[#0A0A0A] border border-[#C8A24A]/10 flex-shrink-0">
                  {it.cover ? (
                    <img src={imageUrl(it.cover)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#C8A24A]/30">
                      <House size={16} />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{it.title}</p>
                  <p className="text-xs text-[#C8A24A]/60 flex items-center gap-1 truncate">
                    <MapPin size={9} weight="fill" /> {it.city || "—"} · {formatMXN(it.price)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-heading font-bold text-[#C8A24A]">{it[metricKey]}</p>
                  <p className="text-xs uppercase tracking-widest text-[#C8A24A]/50">{metricLabel}</p>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function EvolutionChart({ series }) {
  const max = Math.max(1, ...series.map(d => d.leads));
  const width = 720;
  const height = 180;
  const padding = { top: 12, right: 12, bottom: 26, left: 32 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  if (series.length === 0) return null;

  const pointX = (i) => padding.left + (i / Math.max(1, series.length - 1)) * chartW;
  const pointY = (v) => padding.top + chartH - (v / max) * chartH;

  const path = series.map((d, i) => `${i === 0 ? "M" : "L"} ${pointX(i)} ${pointY(d.leads)}`).join(" ");
  const area = `${path} L ${pointX(series.length - 1)} ${padding.top + chartH} L ${pointX(0)} ${padding.top + chartH} Z`;

  // Ticks
  const yTicks = 4;
  const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((max * i) / yTicks));
  // Show ~6 x-axis labels
  const xLabels = series.filter((_, i) => i % Math.max(1, Math.floor(series.length / 6)) === 0);

  const totalLeads = series.reduce((a, b) => a + b.leads, 0);
  const totalWon = series.reduce((a, b) => a + b.won, 0);

  return (
    <div data-testid="evolution-chart">
      <div className="flex gap-6 mb-3 text-xs">
        <span className="text-[#C8A24A]/70">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#C8A24A] mr-1.5 align-middle" />
          Leads recibidos ({totalLeads})
        </span>
        <span className="text-[#C8A24A]/70">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400 mr-1.5 align-middle" />
          Ganados ({totalWon})
        </span>
      </div>
      <div className="w-full overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ minWidth: 500 }} className="text-[#C8A24A]">
          {yTickVals.map((v, i) => {
            const y = pointY(v);
            return (
              <g key={i}>
                <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="rgba(200,162,74,0.08)" />
                <text x={padding.left - 8} y={y + 3} fontSize="10" fill="rgba(200,162,74,0.5)" textAnchor="end">
                  {v}
                </text>
              </g>
            );
          })}
          <defs>
            <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C8A24A" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#C8A24A" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#area-grad)" />
          <path d={path} fill="none" stroke="#C8A24A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {series.map((d, i) => (
            d.won > 0 && (
              <circle key={i} cx={pointX(i)} cy={pointY(d.leads)} r="3" fill="#34D399" stroke="#0A0A0A" strokeWidth="1.5" />
            )
          ))}
          {xLabels.map((d) => {
            const i = series.indexOf(d);
            return (
              <text
                key={d.date}
                x={pointX(i)}
                y={height - 6}
                fontSize="10"
                fill="rgba(200,162,74,0.5)"
                textAnchor="middle"
              >
                {formatXLabel(d.date)}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function formatXLabel(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
  } catch { return iso; }
}
